import React from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      const { fallback: CustomFallback } = this.props;
      
      if (CustomFallback) {
        return <CustomFallback error={this.state.error} onRetry={this.handleRetry} />;
      }

      return (
        <Card sx={{ margin: 2, backgroundColor: '#fff9ef', border: '1px solid rgba(227, 83, 2, 0.2)' }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 3,
                textAlign: 'center'
              }}
            >
              <ErrorIcon sx={{ fontSize: 48, color: '#d63031', marginBottom: 2 }} />
              <Typography variant="h5" sx={{ marginBottom: 2, color: '#a52b22' }}>
                Something went wrong
              </Typography>
              <Typography variant="body1" sx={{ marginBottom: 3, color: '#6c757d' }}>
                We encountered an unexpected error. This has been logged and we'll investigate.
              </Typography>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box sx={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: 2, 
                  borderRadius: 1, 
                  marginBottom: 2,
                  width: '100%',
                  maxWidth: 600
                }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    <strong>Error:</strong> {this.state.error.toString()}
                  </Typography>
                  {this.state.errorInfo.componentStack && (
                    <Box sx={{ marginTop: 1 }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        <strong>Component Stack:</strong>
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.7rem' }}>
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={this.handleRetry}
                  sx={{ backgroundColor: 'var(--color-brand)', boxShadow: 'none', '&:hover': { backgroundColor: 'var(--color-brand-dark)', boxShadow: 'none' } }}
                >
                  Try Again
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => window.location.reload()}
                  sx={{ borderColor: 'var(--color-brand)', color: 'var(--color-brand)' }}
                >
                  Reload Page
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
